import { FC, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEnrollInTargetDegree } from '../../hooks/useEnrollInTargetDegree';
import { PathStep, PathStepModel } from './PathStep';

interface EnrollmentPathCardProps {
  wizardComplete: boolean;
  planSaved: boolean;
}

export const EnrollmentPathCard: FC<EnrollmentPathCardProps> = ({ wizardComplete, planSaved }) => {
  const navigate = useNavigate();
  const steps = useMemo<PathStepModel[]>(
    () => [
      {
        number: 1,
        title: 'Add your education info',
        description:
          'Tell us about your past studies and goals so we can plan your transfer credits.',
        ctaLabel: 'Add my info',
        onAction: () => navigate({ to: '/transcripts' }),
        done: wizardComplete,
      },
      {
        number: 2,
        title: 'Build your course plan',
        description: 'Choose courses that move you toward graduation.',
        ctaLabel: 'Build my plan',
        onAction: () => navigate({ to: '/course-builder' }),
        done: planSaved,
      },
      {
        number: 3,
        title: 'Start your learning journey',
        description:
          "You're one step away from starting your studies. Complete your payment to get started",
        ctaLabel: 'Get Started',
        onAction: () => navigate({ to: '/subscribe' }),
        done: false,
      },
    ],
    [wizardComplete, planSaved, navigate]
  );

  const firstIncomplete = steps.findIndex((step) => !step.done);

  return (
    <section className="flex h-full flex-col gap-5 rounded-[26px] bg-(--neutral-white-NW1000) p-7 shadow-[0_0_4px_rgba(0,0,0,0.25)]">
      <div className="flex flex-col gap-1 lg:gap-2">
        <p className="bold m-0! [font-family:var(--font-montserrat)] text-[10px] tracking-[1.8px] text-[#7e8b96] uppercase">
          Your path to enrollment
        </p>
        <h2 className="m-0! text-lg! bold text-(--neutral-black-NB900)! lg:[font-size:var(--fs-h5)]!">
          Complete these steps to start your journey
        </h2>
      </div>

      <ul className="m-0! flex list-none flex-col">
        {steps.map((step, index) => (
          <PathStep
            key={step.number}
            step={step}
            current={index === firstIncomplete}
            isLast={index === steps.length - 1}
          />
        ))}
      </ul>
    </section>
  );
};
